import axios from "axios";
import { getResolvedApiBase } from "config/apiRoot";
import { logAxiosError } from "utils/errorLog";
import { store } from "store";
import { setUserAuthenticated, setIsLogout } from "store/reducers/auth.slice";
import { setMarketWatchList } from "store/reducers/marketWatch.slice";
import {
  applyAxiosRequestAuth,
  refreshAuthCookiesOnce,
} from "utils/authCookies";

const SESSION_KEYS = ["TradeSession", "DeviceType", "TradeAuthoritySession"];

function isAuthEndpoint(url) {
  const u = String(url || "");
  return (
    u.includes("user/login") ||
    u.includes("user/refresh-token") ||
    u.includes("user/logout") ||
    u.includes("user/register")
  );
}

function shouldAttemptTokenRefresh(status, original, url) {
  if (status !== 401 || !original || original.__fxbRetried401) return false;
  if (!sessionStorage.getItem("TradeSession")) return false;
  if (isAuthEndpoint(url)) return false;
  return true;
}

function clearAuthSession() {
  for (const key of SESSION_KEYS) {
    sessionStorage.removeItem(key);
  }
}

/** Client-side session teardown when the API rejects the token (no server log-out call). */
function forceClientLogout() {
  clearAuthSession();
  delete axios.defaults.headers.common.Authorization;
  store.dispatch(setUserAuthenticated(false));
  store.dispatch(setIsLogout(true));
  store.dispatch(setMarketWatchList());
}

// UI (fxbazzar.com) and API (:2025) are different origins — cookies require credentials on every call.
axios.defaults.withCredentials = true;

// Set before any screen mounts (Script Manager can load before App.js Firebase effect).
const primedApiBase = getResolvedApiBase("");
if (primedApiBase) {
  axios.defaults.baseURL = primedApiBase;
}

axios.interceptors.request.use(
  (config) => applyAxiosRequestAuth(config),
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    logAxiosError(error);

    const status = error.response?.status;
    const original = error.config;
    const url = String(original?.url || "");

    if (shouldAttemptTokenRefresh(status, original, url)) {
      original.__fxbRetried401 = true;
      const refreshed = await refreshAuthCookiesOnce();
      if (refreshed) {
        try {
          applyAxiosRequestAuth(original);
          return await axios.request(original);
        } catch (e) {
          return Promise.reject(e);
        }
      }
    }

    if (
      status === 401 &&
      sessionStorage.getItem("TradeSession") &&
      !isAuthEndpoint(url)
    ) {
      forceClientLogout();
    }
    return Promise.reject(error);
  }
);
